import type { ProtectionView, SemanticPolicyPatch } from "./contracts";

const requestSource = "clearguard-portal";
const responseSource = "clearguard-extension";

interface BridgeResponse<T> {
  source: typeof responseSource;
  challenge: string;
  requestID: string;
  ok: boolean;
  payload?: T;
  error?: string;
}

function randomID(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(24));
  return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
}

function bridgeRequest<T>(action: string, payload?: unknown, timeout = 2200): Promise<T> {
  const challenge = randomID();
  const requestID = randomID();
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      window.removeEventListener("message", receive);
      reject(new Error("companion-unavailable"));
    }, timeout);
    function receive(event: MessageEvent<BridgeResponse<T>>) {
      if (event.source !== window || event.origin !== window.location.origin ||
          event.data?.source !== responseSource || event.data.challenge !== challenge ||
          event.data.requestID !== requestID) return;
      window.clearTimeout(timer);
      window.removeEventListener("message", receive);
      if (!event.data.ok) reject(new Error(event.data.error || "companion-error"));
      else resolve(event.data.payload as T);
    }
    window.addEventListener("message", receive);
    window.postMessage({ source: requestSource, challenge, requestID, action, payload }, window.location.origin);
  });
}

export const companion = {
  connect: (bridgeToken: string) => bridgeRequest<{ accountMatches: boolean; companionVersion: string; syncState?: "ready" | "approval-required" | "unavailable" }>("connect", { bridgeToken }, 10_000),
  protection: (bridgeToken: string) => bridgeRequest<ProtectionView>("protection-view", { bridgeToken }, 10_000),
  patch: (bridgeToken: string, patch: SemanticPolicyPatch) => bridgeRequest<{ revision: number; state: "applied" | "pending-approval" | "delayed" }>("policy-patch", { bridgeToken, patch }, 12_000),
  open: () => bridgeRequest<{ opened: true }>("open-companion")
};
